import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useDebouncedValue, useLocalStorageState } from '@components/component-helpers';
import { ExpandableSearch } from '@components/expandable-search';
import { StateTabs } from '@components/header/state-tabs';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDownload } from '@components/icons/download';
import { SearchBox } from '@components/search-box';
import { SortMenu } from '@components/sort-menu';
import { Stack } from '@components/stack';
import { useIsMobile } from '@hooks/use-is-mobile';
import { useScopedTranslate } from '@locale/translate';
import { storage } from '@utils/browser';

import { Header, MobileActions } from 'components/headers';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';
import { Jobs } from 'pages/Jobs';
import { JOB_TAB_ICONS } from 'pages/Jobs/job-tab-icons';
import {
  JOB_ORDERING_OPTIONS,
  JOB_ORDERING_SHORT_KEY,
  JOB_TAB_DISPLAY_ORDER,
  JOB_TABS,
  JOBS_ORDER_STORAGE_KEY,
  JOBS_SEARCH_STORAGE_KEY,
  JOBS_TAB_STORAGE_KEY,
  jobsLocale,
  parseStoredTab,
  resolveJobOrdering,
} from 'pages/Jobs/jobs-config';
import type { JobListOrdering, JobState } from 'types/jobs';

const SEARCH_DEBOUNCE_MS = 400;

const JobList = () => {
  const { t } = useStore();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState(() => parseStoredTab(storage.get(JOBS_TAB_STORAGE_KEY)));
  // Persisted so returning keeps the search. Only the query value is debounced, or the field drops keystrokes.
  const [searchStr, setSearchStr] = useLocalStorageState<string>({
    key: JOBS_SEARCH_STORAGE_KEY,
    defaultValue: '',
  });
  const debouncedSearch = useDebouncedValue(searchStr, SEARCH_DEBOUNCE_MS);
  const [orderingByTab, setOrderingByTab] = useLocalStorageState<
    Partial<Record<JobState, JobListOrdering>>
  >({
    key: JOBS_ORDER_STORAGE_KEY,
    defaultValue: {},
  });

  const status = JOB_TABS[tab];
  const ordering = resolveJobOrdering({ status, stored: orderingByTab[status], isMobile });

  const changeTab = (newStatus: JobState) => {
    const index = JOB_TABS.indexOf(newStatus);
    setTab(index);
    storage.set(JOBS_TAB_STORAGE_KEY, String(index));
  };

  const tabs = JOB_TAB_DISPLAY_ORDER.map((tabStatus) => {
    const Icon = JOB_TAB_ICONS[tabStatus];
    return { id: tabStatus, label: t(`jobs.tabs.${tabStatus}`), icon: <Icon /> };
  });

  return (
    <>
      <Header
        mobileTitle={t('navigation.jobs')}
        breadcrumbs={<Breadcrumb items={[{ label: t('navigation.jobs') }]} />}
        actions={
          <JobActions
            searchStr={searchStr}
            onSearchChange={setSearchStr}
            ordering={ordering}
            status={status}
            onOrderingChange={(newOrdering) =>
              setOrderingByTab((prev) => ({ ...prev, [status]: newOrdering }))
            }
          />
        }
        tabs={
          <StateTabs
            ariaLabel={t('jobs.tabs.label')}
            tabs={tabs}
            activeTab={status}
            onTabChange={(id) => changeTab(id as JobState)}
          />
        }
      />
      <Jobs
        searchStr={debouncedSearch}
        ordering={ordering}
        status={status}
        onClearSearch={() => setSearchStr('')}
      />
    </>
  );
};

export default JobList;

type JobActionsProps = {
  searchStr: string;
  onSearchChange: (value: string) => void;
  ordering: JobListOrdering;
  status: JobState;
  onOrderingChange: (ordering: JobListOrdering) => void;
};

const JobActions = ({
  searchStr,
  onSearchChange,
  ordering,
  status,
  onOrderingChange,
}: JobActionsProps) => {
  const { t } = useStore();
  const { t: tSort } = useScopedTranslate(jobsLocale);
  const { showModal } = useModal();
  const isMobile = useIsMobile();
  const { hasRolePermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  if (isMobile) {
    return (
      <MobileActions>
        <SearchBox
          placeholder={t('jobs.search.placeholder')}
          value={searchStr}
          onChange={onSearchChange}
          isFullWidth
        />
      </MobileActions>
    );
  }

  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch
        value={searchStr}
        onChange={onSearchChange}
        placeholder={t('jobs.search.placeholder')}
      />
      <SortMenu
        options={JOB_ORDERING_OPTIONS[status]}
        value={ordering}
        onChange={onOrderingChange}
        getOptionLabel={(option) => tSort(`sort_${option}`)}
        getButtonLabel={(option) => tSort(JOB_ORDERING_SHORT_KEY[option])}
        dataTestid="order-jobs-btn"
      />
      <Button
        variant="secondary"
        text={t('jobs.export')}
        iconRight={<IconDownload />}
        onClick={() => showModal('EXPORT_JOBS')}
      />
      {hasRolePermission('job:create') && (
        <Button
          iconLeft={<IconAddPlus />}
          text={t('jobs.createjob')}
          onClick={() => navigate('/job/create', { state: { from: location.pathname } })}
          dataCy="navigate-job-create"
        />
      )}
    </Stack>
  );
};
